import type { FlowStatus, FlowSummary, FlowType } from "../types";
import { pluralize } from "@/utils/pluralize";

type StatusTone = "open" | "success" | "warning" | "danger" | "muted";

/** A missing status fails `tsc` instead of rendering a blank badge. */
export const FLOW_STATUS_META: Record<FlowStatus, { label: string; tone: StatusTone }> = {
  open: { label: "Open", tone: "open" },
  completed: { label: "Completed", tone: "success" },
  expired: { label: "Expired", tone: "warning" },
  invalidated: { label: "Invalidated", tone: "muted" },
  merchant_declined_agreement: { label: "Declined", tone: "danger" },
};

/** Same labels as the backend registry recipes. */
export const FLOW_TYPE_LABELS: Record<FlowType, string> = {
  onboarding: "Merchant Onboarding",
  bank_connection: "Bank Connection",
  account_selector: "Primary Account Selector",
  standalone_contract: "Standalone Contract",
  id_verification: "ID Verification",
  refinance: "Refinance",
  addendum: "Addendum",
  document_request: "Document Request",
  one_off_contract: "Custom upload",
  document: "Document",
};

/** A Custom upload is named by the document it sent; every other flow by its type. */
export function flowLabel(flow: Pick<FlowSummary, "flow_type" | "custom_upload">): string {
  return flow.custom_upload?.name || FLOW_TYPE_LABELS[flow.flow_type];
}

export const STATUS_TONE_CLASSES: Record<StatusTone, { badge: string; dot: string }> = {
  open: {
    badge:
      "border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-200",
    dot: "bg-sky-500",
  },
  success: {
    badge:
      "border-green-200 bg-green-50 text-green-800 dark:border-green-900 dark:bg-green-950/40 dark:text-green-200",
    dot: "bg-green-500",
  },
  warning: {
    badge:
      "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200",
    dot: "bg-amber-500",
  },
  danger: {
    badge: "border-destructive/30 bg-destructive/10 text-destructive",
    dot: "bg-destructive",
  },
  muted: { badge: "border-border bg-muted text-muted-foreground", dot: "bg-muted-foreground/50" },
};

export function documentCountLabel(count: number): string {
  return `${count} ${pluralize(count, "document", "documents")}`;
}

/** "Sent …" completion. A null sender is the automated Plaid send (or a deleted user). */
export function sentByPhrase(flow: { created_by: { name: string } | null }): string {
  if (flow.created_by == null) return "Sent automatically";
  return `Sent by ${flow.created_by.name.trim() || "an unnamed user"}`;
}
